import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";
import { registerUserManager } from "../config/axios";

// ── Types ─────────────────────────────────────────────────────────────────────
interface UserProfile {
  _id: string | null;
  id: string | null;
  keycloakId?: string;
  username: string;
  email: string;
  role: string;
  accountStatus: string;
  profile?: {
    displayName?: string;
    avatar?: string;
    bio?: string;
    banner?: string;
    website?: string;
    followers?: number;
    following?: number;
    likes?: number;
    isVerified?: boolean;
  };
  [key: string]: unknown;
}

interface AuthContextType {
  user: UserProfile | null;
  loading: boolean;
  authenticating: boolean;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => Promise<void>;
  updateUser: (user: UserProfile) => void;
  refreshUser: () => Promise<void>;
  getAccessToken: () => string | null;
}

// ── Context ───────────────────────────────────────────────────────────────────
const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

/**
 * Helper to grab token from LOCAL STORAGE.
 * This ensures the session is shared across tabs.
 */
const getToken = (): string | null => {
  try {
    const oidcKey = Object.keys(localStorage).find((k) =>
      k.startsWith("oidc.user:"),
    );
    if (!oidcKey) return null;
    const raw = localStorage.getItem(oidcKey);
    if (!raw) return null;
    const oidcData = JSON.parse(raw);
    return oidcData?.access_token || null;
  } catch {
    return null;
  }
};

/**
 * Clear all OIDC state from localStorage.
 */
const clearOidcStorage = () => {
  Object.keys(localStorage)
    .filter((k) => k.startsWith("oidc.user:"))
    .forEach((k) => localStorage.removeItem(k));
};

// ── Provider ──────────────────────────────────────────────────────────────────
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const auth = useOidcAuth();
  const [localUser, setLocalUser] = useState<UserProfile | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncComplete, setSyncComplete] = useState(false);
  const hasSynced = useRef(false);
  const lastTokenSync = useRef<string | null>(null);

  // ── Register OIDC UserManager with axios interceptor ──────────────────────
  useEffect(() => {
    const um = (auth as any)?.settings?._userManager ?? null;
    registerUserManager(um);
    return () => {
      registerUserManager(null);
    };
  }, [auth]);

  // ── OIDC token event listeners ────────────────────────────────────────────
  useEffect(() => {
    const events = auth.events;
    if (!events) return;

    const handleTokenExpired = () => {
      console.warn("[Auth] Access token expired — clearing session.");
      clearOidcStorage();
      window.location.href = "/";
    };

    const handleSilentRenewError = (error: Error) => {
      console.warn("[Auth] Silent renewal failed:", error.message);
      clearOidcStorage();
      window.location.href = "/";
    };

    const handleUserLoaded = () => {
      // Seamlessly updated auth.user without reloading the page.
      // The context state will naturally propagate down to consumers.
      console.log("[Auth] User loaded/silently authenticated. Seamlessly updating states.");
    };

    events.addAccessTokenExpired(handleTokenExpired);
    events.addSilentRenewError(handleSilentRenewError);
    events.addUserLoaded(handleUserLoaded);

    return () => {
      events.removeAccessTokenExpired(handleTokenExpired);
      events.removeSilentRenewError(handleSilentRenewError);
      events.removeUserLoaded(handleUserLoaded);
    };
  }, [auth.events]);

  /**
   * Multi-tab sync logic.
   */
  useEffect(() => {
    const handleStorageChange = async (e: StorageEvent) => {
      if (e.key && e.key.startsWith('oidc.user:')) {
        if (!e.newValue) {
          console.log("[Auth] Session cleared in another tab. Reloading...");
          window.location.reload();
        } else {
          console.log("[Auth] Session updated in another tab. Syncing state...");
          try {
            // Force react-oidc-context to pick up the new user from storage
            // This safely syncs state across tabs without reload!
            await auth.signinSilent();
          } catch (error) {
            console.error("Silent sync failed across tabs", error);
            // Fallback to reload if silent sync fails
            window.location.reload(); 
          }
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [auth]);

  useEffect(() => {
    if (!auth.isAuthenticated && !auth.isLoading) {
      setSyncComplete(true);
      return;
    }

    // Only prevent sync if we already synced this specific token
    const currentToken = getToken();
    if (!auth.isAuthenticated || !auth.user || (hasSynced.current && lastTokenSync.current === currentToken)) return;

    const syncWithBackend = async () => {
      setSyncing(true);
      hasSynced.current = true;
      lastTokenSync.current = currentToken;

      try {
        if (!currentToken) throw new Error("No access token found in localStorage");

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/auth/sync`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${currentToken}`,
            },
            credentials: "include",
          },
        );

        if (response.status === 401) {
          console.warn("[Auth] Sync returned 401 — session expired, redirecting to login.");
          clearOidcStorage();
          window.location.href = "/";
          return;
        }

        if (!response.ok) throw new Error(`Sync failed: ${response.status}`);

        const data = await response.json();
        setLocalUser(data.user as UserProfile);
      } catch (error) {
        console.error("Backend sync failed, using Keycloak fallback:", error);
        const profile = auth.user?.profile;
        if (profile) {
          setLocalUser({
            _id: null,
            id: null,
            keycloakId: profile.sub,
            username: (profile.preferred_username as string) || (profile.name as string) || "",
            email: (profile.email as string) || "",
            role: "user",
            accountStatus: "active",
            profile: {
              displayName: (profile.name as string) || "",
              avatar: "",
            },
          });
        }
      } finally {
        setSyncing(false);
        setSyncComplete(true);
      }
    };

    syncWithBackend();
  }, [auth.isAuthenticated, auth.user, auth.isLoading]);

  useEffect(() => {
    if (!auth.isAuthenticated && !auth.isLoading) {
      setLocalUser(null);
      setSyncComplete(true);
      hasSynced.current = false;
      lastTokenSync.current = null;
    }
  }, [auth.isAuthenticated, auth.isLoading]);

  const logout = async (): Promise<void> => {
    try {
      const token = getToken();
      await fetch(`${process.env.REACT_APP_API_URL}/api/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token ?? ""}` },
        credentials: "include",
      });
    } catch { /* ignore */ }

    auth.signoutRedirect({
      id_token_hint: auth.user?.id_token,
      post_logout_redirect_uri: window.location.origin + "/",
      extraQueryParams: {
        client_id: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || ""
      }
    });
    
    localStorage.clear();
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const token = getToken();
      if (!token) return;
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/verify`,
        {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        },
      );
      if (response.ok) {
        const data = await response.json();
        setLocalUser(data.user as UserProfile);
      }
    } catch (error) {
      console.error("Failed to refresh user:", error);
    }
  };

  if (auth.isLoading || (auth.isAuthenticated && !syncComplete)) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          flexDirection: "column",
          gap: "1rem",
          background: "#fff",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "3px solid #f3f3f3",
            borderTop: "3px solid #291418",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        <p style={{ color: "#666", fontSize: "14px", margin: 0 }}>
          Setting up your session...
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const value: AuthContextType = {
    user: localUser,
    loading: auth.isLoading || syncing,
    authenticating:
      auth.activeNavigator === "signinSilent" ||
      auth.activeNavigator === "signinRedirect",
    isAuthenticated: auth.isAuthenticated,
    login: () => auth.signinRedirect(),
    logout,
    updateUser: (updatedUser: UserProfile) => setLocalUser(updatedUser),
    refreshUser,
    getAccessToken: getToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;